// css-url — monta um valor `url("...")` de CSS a partir de uma URL que veio de
// fora (painel Xtream do provedor, TMDB, qualquer coisa que o catálogo devolva).
//
// ===== POR QUE ISTO EXISTE =====
// As telas montam backdrop/logo assim:
//
//   style={{ backgroundImage: `url("${rewriteImgUrl(x, ...)}")` }}
//
// Enquanto a URL ia pro nosso proxy, ela entrava como parâmetro de query e o
// `URLSearchParams` a percent-encodava: aspas e parênteses nunca chegavam ao
// CSS. Com a TV carregando a imagem DIRETO, a URL do painel entra CRUA na
// string — e uma capa cadastrada como
//
//   http://host/x.jpg");background:url("http://evil/
//
// fecha o `url("...")` e escreve CSS que ninguém autorizou.
//
// ===== NÃO É EXPLORÁVEL HOJE, E MESMO ASSIM SE CORRIGE =====
// React aplica `style` como objeto via CSSOM (`el.style.backgroundImage = ...`),
// e o CSSOM REJEITA o valor inteiro quando ele não parseia — o resultado prático
// de uma URL maliciosa hoje é backdrop nenhum, não CSS injetado. A correção é
// pelo que acontece DEPOIS: o dia em que alguém trocar isso por
// `style="background-image: url(...)"` numa string (ou por
// `insertRule`/`cssText`, que não têm essa rede de proteção), a injeção passa a
// valer — e ninguém vai lembrar que a segurança daquela linha dependia de um
// detalhe de implementação do React. A função certa no lugar certo remove a
// pegadinha em vez de documentá-la.
//
// Escapar > percent-encodar a URL inteira: encodar quebraria URL legítima
// (o `?`/`&` do painel Xtream viram lixo). Aqui só os caracteres que têm
// significado DENTRO do `url("...")` são neutralizados.

/**
 * Escapa uma URL pra dentro de um literal `url("...")` de CSS.
 *
 * Neutraliza o que pode escapar do literal:
 *  - `\` → `\\` (PRIMEIRO, senão a própria escapada seria escapada de novo)
 *  - `"` → `\"` (fecha a string)
 *  - `'` → `\'` (fecha a string na variante de aspas simples)
 *  - `(` `)` → escapados (fecham o `url()` mesmo sem aspas)
 *  - newline/CR/FF → escapados (quebra de linha é ilegal dentro de string CSS e
 *    invalida a declaração inteira)
 *
 * A barra invertida é o mecanismo de escape do PRÓPRIO CSS (CSS Syntax L3
 * §4.3.7), suportado desde sempre — dentro do piso de compat da TV (Chr38).
 */
export function escapeCssUrl(url: string): string {
  return url
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/'/g, "\\'")
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/\n/g, '\\A ')
    .replace(/\r/g, '\\D ')
    .replace(/\f/g, '\\C ')
}

/**
 * Monta o valor completo de `background-image` pra uma URL externa.
 *
 * URL vazia/nula → `'none'`, e não `url("")`: string vazia num `url()` faz o
 * browser resolver contra o documento e **re-baixar a própria página** como se
 * fosse imagem (em `file://` da TV, um erro por card). `'none'` é o valor certo
 * e o CSS de trás (gradiente/cor de fundo) aparece sozinho.
 *
 * É também o ponto onde a capa BLOQUEADA pelo guard de LAN aterrissa: o
 * `rewriteImgUrl` devolve '' e a tela mostra o fundo padrão, sem erro na cara do
 * usuário. Ver lib/image-source.ts.
 */
export function cssUrl(url: string | null | undefined): string {
  if (!url) return 'none'
  return `url("${escapeCssUrl(url)}")`
}
